<script module lang="ts">
    export type Props = {
        value?: { pointGroups: PointGroup[]; svg: string } | null;
        error?: string | null;
        label: string;
        name: string;
        description?: string;
        signaturePadOptions?: Record<string, unknown>;
    };
</script>

<script lang="ts">
    import InputError from '@/components/InputError.svelte';
    import { Label } from '@/components/ui/label/index.js';
    import { Trash2 } from 'lucide-svelte';
    import FieldDescription from '@/components/Form/Inputs/FieldDescription.svelte';
    import SignaturePad, { type PointGroup } from 'signature_pad';
    import { onMount } from 'svelte';
    import Button from '../ui/button/button.svelte';

    let {
        value = $bindable(),
        error,
        label,
        name,
        description,
        signaturePadOptions = {},
    }: Props = $props();

    // @ts-expect-error val is only used in the on mount fn
    let canvas: HTMLCanvasElement = $state();
    // @ts-expect-error val is only used in the on mount fn
    let signaturePad: SignaturePad = $state();

    onMount(() => {
        signaturePad = new SignaturePad(canvas, {
            minWidth: 2,
            maxWidth: 4,
            ...signaturePadOptions,
        });

        if (value) {
            signaturePad.fromData(value.pointGroups);
        }
        const resizeCanvas = () => {
            if (!signaturePad) return;

            const ratio = Math.max(window.devicePixelRatio || 1, 1);
            canvas.width = canvas.offsetWidth * ratio;
            canvas.height = canvas.offsetHeight * ratio;

            canvas.getContext('2d')?.scale(ratio, ratio);
            if (value) {
                signaturePad.fromData(value.pointGroups);
            }
        };
        const resizeObs = new ResizeObserver(() => {
            resizeCanvas();
        });
        resizeObs.observe(canvas);
        window.addEventListener('resize', resizeCanvas);

        const saveVal = () => {
            value = {
                pointGroups: signaturePad.toData(),
                svg: signaturePad.toSVG(),
            };
        };
        signaturePad.addEventListener('endStroke', saveVal);

        return () => {
            window.removeEventListener('resize', resizeCanvas);
            signaturePad.off();
            resizeObs.disconnect();
        };
    });

    const clear = () => {
        signaturePad.clear();
        value = null;
    };
</script>

<div class="grid gap-2">
    <Label for={name}>{label}</Label>
    <FieldDescription>{description}</FieldDescription>
    {#if description}
        <FieldDescription>{description}</FieldDescription>
    {/if}
    <div class="relative h-[200px] rounded border">
        <Button variant="destructive" class=" absolute top-2 right-2 z-10" onclick={clear}>
            <Trash2 class="h-5 w-5 " />
        </Button>
        <canvas bind:this={canvas} class="absolute top-0 left-0 h-full w-full rounded bg-white"
        ></canvas>
    </div>
    {#if error}
        <InputError class="mt-2" message={error} />
    {/if}
</div>
